import { Theme } from "@shared/lib/constants/themes";
import { Hsluv } from "hsluv";
import { MaybeRefOrGetter, ref, Ref, toValue, watch } from "vue";

export default function (
  elRef: MaybeRefOrGetter<{ $el: HTMLElement } | HTMLElement | null | undefined>,
  theme: Ref<keyof typeof Theme | null | undefined> = ref(),
  callback?: (options: {
    primaryColor: string,
    secondaryColor: string,
  }) => void,
) {
  watch([() => resolveEl(elRef), theme], () => {
    applyTheme();
  }, {
    immediate: true,
  });

  function resolveEl (_elRef: MaybeRefOrGetter<{ $el: HTMLElement } | HTMLElement | null | undefined>): HTMLElement | null {
    const value = toValue(_elRef);
    if (!value) return null;
    if (value instanceof HTMLElement) return value;
    return (value.$el as HTMLElement) ?? null;
  }

  function applyTheme () {
    const el = resolveEl(elRef);
    if (!el) return;

    el.style.setProperty("--themeColorPrimary", `var(--themeColorPrimary${theme.value}, var(--themeColorPrimaryLosAngelesLakers))`);
    el.style.setProperty("--themeColorSecondary", `var(--themeColorSecondary${theme.value}, var(--themeColorSecondaryLosAngelesLakers))`);

    const primaryColor = getComputedStyle(el).getPropertyValue("--themeColorPrimary");
    const secondaryColor = getComputedStyle(el).getPropertyValue("--themeColorSecondary");
    el.style.setProperty("--themeColorSecondaryForeground", isDarkColor(secondaryColor) ? "var(--themeColorNeutral0)" : "var(--themeColorNeutral900)");

    callback?.({
      primaryColor,
      secondaryColor,
    });
  }

  function isDarkColor (color: string) {
    // YIQ equation from http://24ways.org/2010/calculating-color-contrast
    const hsluv = new Hsluv();
    hsluv.hex = color;
    hsluv.hexToRgb();
    const [
      red,
      green,
      blue,
    ] = [
      hsluv.rgb_r,
      hsluv.rgb_g,
      hsluv.rgb_b,
    ].map((value) => value * 255);
    const yiq = (red * 299 + green * 587 + blue * 114) / 1000;

    return yiq < 128;
  }

  return {
    theme,
    isDarkColor,
  };
}
